import type { BlobInfo, ICaseDataRepository } from '@/engine/app/CaseDataProvider';
import type { NewPackageInfo } from '@/engine/app/CaseFilesApi';
import type { CaseInfo } from '@/engine/app/CaseInfoApi';
import type { OfflineDB } from './OfflineDB';
import { CaseContext } from '@/engine/app/CasePersistance/CaseContext';

export class OfflineCaseDataRepository implements ICaseDataRepository {
  private _db: OfflineDB;

  private _$caseContext?: CaseContext;

  public constructor(offlineDb: OfflineDB) {
    this._db = offlineDb;
  }

  public uploadBlob(): Promise<Response> {
    throw new Error('Method not implemented.');
  }

  public derivePackage(): Promise<NewPackageInfo> {
    throw new Error('Method not implemented.');
  }

  public saveDerivedPackage(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  public approveDerivedPackage(): Promise<string> {
    throw new Error('Method not implemented.');
  }

  public rejectDerivedPackage(): Promise<string> {
    throw new Error('Method not implemented.');
  }

  private get _caseContext(): CaseContext {
    if (!this._$caseContext) {
      throw new Error('Case context is not set');
    }
    return this._$caseContext;
  }

  public setContext(caseId: string, packageId: string): void {
    this._$caseContext = new CaseContext({ caseId, packageId });
  }

  public getCaseBlobUrlResolver(): (descriptor: string) => Promise<string> {
    return async (descriptor: string) => {
      return this._db.schema.blobs.get([this._caseContext.packageId, descriptor]).then((blobInfo) => {
        if (!blobInfo) {
          throw new Error('Blob not found');
        }
        return blobInfo.url;
      });
    };
  }

  public async hasCaseBlobInfo(descriptor: string): Promise<boolean> {
    return this._db.schema.blobs.get([this._caseContext.packageId, descriptor]).then((blobInfo) => !!blobInfo);
  }

  public async getCaseBlob(descriptor: string): Promise<ArrayBuffer> {
    return this._db.schema.blobs.get([this._caseContext.packageId, descriptor]).then((blobInfo) => {
      if (!blobInfo) {
        throw new Error('Blob not found');
      }
      return fetch(blobInfo.url).then((response) => response.arrayBuffer());
    });
  }

  public async getCaseFilesList(): Promise<Array<BlobInfo>> {
    const packageFiles = await this._db.schema.packageFiles.get(this._caseContext.packageId);
    if (!packageFiles) {
      return Promise.reject(new Error('Package not found'));
    }

    return Promise.all(
      packageFiles.files.map(async (descriptor) => {
        const blobInfo = await this._db.schema.blobs.get([this._caseContext.packageId, descriptor]);

        return blobInfo;
      })
    ).then((blobInfos) => blobInfos.filter((blobInfo): blobInfo is BlobInfo & { packageId: string } => !!blobInfo));
  }

  public async getCaseFileInfo(fileDescriptor: string): Promise<BlobInfo> {
    const fileInfo = await this._db.schema.blobs.get([this._caseContext.packageId, fileDescriptor]);

    if (!fileInfo) {
      return Promise.reject(new Error('Blob not found'));
    }
    return fileInfo;
  }

  public async getCaseInfo(): Promise<CaseInfo> {
    const caseInfo = await this._db.schema.cases.get(this._caseContext.caseId);
    if (!caseInfo) {
      return Promise.reject(new Error('Case not found'));
    }
    return caseInfo;
  }
}
